/*
 * The search loader (run 24, Levan 11.10.2026): no full-screen sky any more - one thin line above the results, themed per page
 * (hotels: a car on the road to a hotel; tours: a plane flying to a resort; excursions: a car to an amusement park; cruises: a liner
 * sailing to an island; flights: a plane to the landing strip ...). Docs: docs features/website/search-line-loader.md.
 * `active` omitted = follows LoadingWindowService (the show() / hide() every search form already calls). `progress` 0..1 puts the
 * traveller where the search is; omitted = one slow pass towards 90 % that waits there (R-LL3a). Reduced motion: the traveller waits at the start.
 * `fallback`: the one copy in the app shell, drawn only while a search runs and no page placed its own line.
 */
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, map } from 'rxjs';
import { LoadingWindowService } from '../loading-window/loading-window.service';
import { SEARCH_LOADER_THEMES, SearchLoaderTheme, searchLoaderThemeOf } from './search-loader-theme';

@Component({
  selector: 'app-search-line-loader',
  imports: [TranslatePipe, NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.hidden]': '!shown()', '[class.sll-fallback]': 'fallback()' },
  template: `
    @if (shown()) {
      <div
        class="sll"
        role="status"
        aria-live="polite"
        data-testid="search-line-loader"
        [attr.data-theme]="themeName()"
        [class.sll-indeterminate]="share() === null"
      >
        <span class="sr-only">{{ label() | translate }}</span>
        <div
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          [class]="'sll-track sll-' + spec().path"
          [attr.aria-valuenow]="percent()"
          [attr.aria-label]="label() | translate"
        >
          <div class="sll-done" [style.width.%]="percent()"></div>
          <div class="sll-traveller" data-testid="search-line-loader-traveller" [style.left.%]="percent()">
            <ng-container *ngTemplateOutlet="icon; context: { $implicit: spec().traveller }" />
          </div>
        </div>
        <div class="sll-destination" data-testid="search-line-loader-destination">
          <ng-container *ngTemplateOutlet="icon; context: { $implicit: spec().destination }" />
        </div>
      </div>
    }

    <ng-template #icon let-name>
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" [attr.data-icon]="name">
        @switch (name) {
          @case ('car') {
            <path
              fill-rule="evenodd"
              d="M2 16.2v-3.4c0-.5.3-.9.8-1l2.4-.8 2.2-3.4c.3-.4.7-.6 1.1-.6h6.2c.4 0 .8.2 1 .5l2.6 3.5h2c1 0 1.7.8 1.7 1.7v3.5h-1.9a2.6 2.6 0 0 0-5.1 0H8.8a2.6 2.6 0 0 0-5.1 0zM8.7 8.4 7.3 10.8H11V8.4zm3.8 0v2.4h4.1l-1.8-2.4z"
            />
            <circle cx="6.2" cy="16.6" r="1.9" />
            <circle cx="17.4" cy="16.6" r="1.9" />
          }
          @case ('plane') {
            <path
              transform="rotate(90 12 12)"
              d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"
            />
          }
          @case ('ship') {
            <path
              fill-rule="evenodd"
              d="M2.5 14.5h19l-2.4 4.3c-.3.5-.8.7-1.3.7H6.3c-.6 0-1.1-.3-1.3-.8zM4.5 13.5v-3h15v3zm1.8-1.9v.9h1.4v-.9zm3 0v.9h1.4v-.9zm3 0v.9h1.4v-.9zm3 0v.9h1.4v-.9zM7 9.5v-3h9.5v3zm2-6h2.5v2H9z"
            />
          }
          @case ('suitcase') {
            <path
              d="M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z"
            />
          }
          @case ('magnifier') {
            <path
              d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
            />
          }
          @case ('hotel') {
            <path
              fill-rule="evenodd"
              d="M3.5 21V4.2c0-.7.5-1.2 1.2-1.2h9.1c.7 0 1.2.5 1.2 1.2V9h3.8c.7 0 1.2.5 1.2 1.2V21h-6v-3.5h-4.5V21zM6.5 6v2h2V6zm4 0v2h2V6zm-4 4v2h2v-2zm4 0v2h2v-2zm-4 4v2h2v-2zm4 0v2h2v-2zm6-2v2h2v-2zm0 4v2h2v-2z"
            />
          }
          @case ('resort') {
            <circle cx="19.5" cy="4.5" r="2" />
            <path
              d="M11.2 21c.2-3.4.9-6.9 2.1-10.1l1.5.4c-1.1 3.2-1.7 6.4-1.9 9.7zM13.6 10.6C11.4 8.3 8 8 5.3 9.4c2.6.1 5.3.6 8.3 1.2zM13.9 10.2c-.7-3 .6-5.6 3.5-7.2-1.1 2.2-2.1 4.6-3.5 7.2zM14.3 10.7c2.6-1.3 5.6-.9 7.5.9-2.5-.3-5-.6-7.5-.9zM13.7 10.8c-2.9-.1-5.3 1.4-6.2 4 1.7-1.6 3.8-2.9 6.2-4zM2.5 22v-.9c2.7-1 6-1.5 9.5-1.5s6.8.5 9.5 1.5v.9z"
            />
          }
          @case ('park') {
            <path
              d="M12 2.5a7.5 7.5 0 1 0 0 15 7.5 7.5 0 0 0 0-15zm0 1.6c.6 0 1.2.1 1.8.2L12.6 8.6a1.6 1.6 0 0 0-1.2 0L10.2 4.3c.6-.1 1.2-.2 1.8-.2zm-3.4.8 1.3 4.2c-.3.3-.5.6-.6 1L5.1 9.7a6 6 0 0 1 3.5-4.8zm6.8 0a6 6 0 0 1 3.5 4.8l-4.2.4c-.1-.4-.3-.7-.6-1zM5.2 11.3l4.2-.3c.1.3.3.6.6.8l-2.1 3.8a6 6 0 0 1-2.7-4.3zm13.6 0a6 6 0 0 1-2.7 4.3L14 11.8c.3-.2.5-.5.6-.8zM12 12.6l2.2 3.7c-.7.3-1.4.4-2.2.4s-1.5-.1-2.2-.4z"
            />
            <path d="M11.2 10.3h1.6L18 21.5h-1.8l-1.3-3h-5.8l-1.3 3H6zm-1.4 6.6h4.4L12 12.1z" />
          }
          @case ('island') {
            <circle cx="18.5" cy="5" r="1.8" />
            <path
              d="M10.8 17c.1-2.6.6-5.2 1.6-7.6l1.3.4c-.9 2.3-1.3 4.7-1.5 7.2zM12.9 9.7c-1.8-1.7-4.4-1.9-6.4-.8 2-.1 4 .4 6.4.8zM13.1 9.4c-.5-2.3.5-4.2 2.7-5.4-.9 1.7-1.6 3.4-2.7 5.4zM13.4 9.8c2-1 4.2-.7 5.6.7-1.9-.2-3.7-.5-5.6-.7zM1.5 21.5c2.4-3.2 6.2-5 10.5-5s8.1 1.8 10.5 5z"
            />
          }
          @case ('landing') {
            <path
              d="M2.5 19h19v2h-19zm7.18-5.73 4.35 1.16 5.31 1.42c.8.21 1.62-.26 1.84-1.06.21-.8-.26-1.62-1.06-1.84l-5.31-1.42-2.76-9.02L10.12 2v8.28L5.15 8.95l-.93-2.32-1.45-.39v5.17l1.6.43z"
            />
          }
          @case ('pin') {
            <path
              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5z"
            />
          }
          @case ('shield') {
            <path
              d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"
            />
          }
          @case ('document') {
            <path
              d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"
            />
          }
        }
      </svg>
    </ng-template>
  `,
  styles: `
    :host {
      display: block;
      --sll-accent: var(--kz-color-primary, #f08c00);
      --sll-line: color-mix(in srgb, var(--kz-color-text, #475467) 22%, transparent);
      --sll-dest: var(--kz-color-text-secondary, #667085);
      --sll-icon: 20px;
    }
    :host(.hidden) {
      display: none;
    }
    :host(.sll-fallback) {
      position: sticky;
      top: 0;
      z-index: 1020;
      padding: 0.25rem 1rem;
      background: color-mix(in srgb, var(--c-ground, #fff) 88%, transparent);
    }
    .sll {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      height: 28px;
      animation: sll-in 200ms ease-out;
    }
    .sll-track {
      position: relative;
      flex: 1;
      height: 3px;
      border-radius: 9999px;
      background: var(--sll-line);
      margin-right: calc(var(--sll-icon) / 2);
    }
    .sll-road {
      height: 4px;
      background:
        repeating-linear-gradient(90deg, var(--c-ground, #fff) 0 6px, transparent 6px 14px) center / 100% 1px no-repeat,
        var(--sll-line);
    }
    .sll-air {
      height: 0;
      background: none;
      border-top: 2px dashed var(--sll-line);
    }
    .sll-sea {
      height: 6px;
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='6' viewBox='0 0 16 6'%3E%3Cpath d='M0 3c2-2.5 6-2.5 8 0s6 2.5 8 0' fill='none' stroke='%2398a2b3' stroke-width='1.4'/%3E%3C/svg%3E")
        repeat-x center / 16px 6px;
    }
    .sll-done {
      position: absolute;
      left: 0;
      top: 50%;
      height: 3px;
      transform: translateY(-50%);
      border-radius: 9999px;
      background: linear-gradient(90deg, color-mix(in srgb, var(--sll-accent) 35%, transparent), var(--sll-accent));
      transition: width 600ms cubic-bezier(0.22, 1, 0.36, 1);
    }
    .sll-traveller {
      position: absolute;
      top: 50%;
      width: var(--sll-icon);
      height: var(--sll-icon);
      transform: translate(-50%, -58%);
      color: var(--sll-accent);
      transition: left 600ms cubic-bezier(0.22, 1, 0.36, 1);
    }
    .sll-destination {
      flex: none;
      width: calc(var(--sll-icon) + 2px);
      height: calc(var(--sll-icon) + 2px);
      color: var(--sll-dest);
    }
    svg {
      display: block;
      width: 100%;
      height: 100%;
      fill: currentColor;
    }
    /* R-LL3a: no share known - one slow pass that eases towards 90 % and waits there, never a bar that fills and starts again
       (Levan 12.10.2026: "loader fills up a couple of times before it shows results" on a 10-20 s flight search) */
    .sll-indeterminate .sll-traveller {
      transition: none;
      animation: sll-travel 20s cubic-bezier(0.12, 0.6, 0.3, 1) forwards;
    }
    .sll-indeterminate .sll-done {
      transition: none;
      animation: sll-fill 20s cubic-bezier(0.12, 0.6, 0.3, 1) forwards;
    }
    .sll-indeterminate .sll-destination {
      animation: sll-arrive 2.8s ease-in-out infinite;
    }
    @keyframes sll-travel {
      0% {
        left: 0%;
        opacity: 0;
      }
      4% {
        opacity: 1;
      }
      100% {
        left: 90%;
        opacity: 1;
      }
    }
    @keyframes sll-fill {
      0% {
        width: 0%;
      }
      100% {
        width: 90%;
      }
    }
    @keyframes sll-arrive {
      0%,
      80% {
        color: var(--sll-dest);
      }
      90% {
        color: var(--sll-accent);
      }
      100% {
        color: var(--sll-dest);
      }
    }
    @keyframes sll-in {
      from {
        opacity: 0;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .sll,
      .sll-done,
      .sll-traveller,
      .sll-indeterminate .sll-traveller,
      .sll-indeterminate .sll-done,
      .sll-indeterminate .sll-destination {
        animation: none;
        transition: none;
      }
      .sll-indeterminate .sll-done {
        width: 0;
      }
      .sll-indeterminate .sll-traveller {
        left: 0;
      }
    }
  `
})
export class SearchLineLoaderComponent implements OnInit {
  readonly #loading = inject(LoadingWindowService);
  readonly #router = inject(Router, { optional: true });

  readonly theme = input<SearchLoaderTheme | null | undefined>(undefined);
  /** omitted = follows LoadingWindowService */
  readonly active = input<boolean | null | undefined>(undefined);
  /** 0..1; omitted / null = indeterminate */
  readonly progress = input<number | null | undefined>(undefined);
  readonly label = input('The search results are loading');
  readonly fallback = input(false, { transform: (v: boolean | string) => v === '' || v === true || v === 'true' });

  readonly #url = this.#router
    ? toSignal(
        this.#router.events.pipe(
          filter((e) => e instanceof NavigationEnd),
          map(() => this.#router!.url)
        ),
        { initialValue: this.#router.url }
      )
    : () => '';

  protected readonly themeName = computed<SearchLoaderTheme>(() => {
    const t = this.theme();
    return t && t in SEARCH_LOADER_THEMES ? t : searchLoaderThemeOf(this.#url());
  });
  protected readonly spec = computed(() => SEARCH_LOADER_THEMES[this.themeName()]);

  protected readonly shown = computed(() => {
    if (this.fallback()) return this.#loading.active() && !this.#loading.hasPageLoader();
    const a = this.active();
    return a === undefined ? this.#loading.active() : !!a;
  });

  protected readonly share = computed<number | null>(() => {
    const p = this.progress();
    return typeof p === 'number' && Number.isFinite(p) ? Math.min(1, Math.max(0, p)) : null;
  });
  protected readonly percent = computed<number | null>(() => {
    const s = this.share();
    return s === null ? null : Math.round(s * 100);
  });

  readonly #destroyRef = inject(DestroyRef);

  /** a line on the page takes over from the shell's fallback line */
  ngOnInit() {
    if (this.fallback()) return;
    this.#destroyRef.onDestroy(this.#loading.registerPageLoader());
  }
}
